<template>
  <!-- Docked Ask Allen — one companion, every surface (spec §3.5). A pinned
       launcher opens a slide-out chat panel over the content. -->
  <div class="allen-dock">
    <!-- Launcher -->
    <button
      v-if="!open"
      type="button"
      class="allen-launch"
      :aria-label="t('askAllen.title')"
      @click="open = true"
    >
      <i class="bi bi-robot"></i>
      <span class="allen-launch-txt">{{ t('askAllen.title') }}</span>
    </button>

    <!-- Backdrop (click to dismiss). Rendered BEFORE the panel so the panel
         stacks above it — otherwise a click inside the panel hits the backdrop
         and closes it. -->
    <div v-if="open" class="allen-backdrop" @click="open = false"></div>

    <!-- Slide-out panel -->
    <transition name="allen-slide">
      <div v-if="open" class="allen-panel" role="dialog" :aria-label="t('askAllen.title')">
        <header class="allen-panel-head">
          <span class="allen-panel-id">
            <span class="allen-panel-avatar"><i class="bi bi-robot"></i></span>
            <span>
              <span class="allen-panel-title">{{ t('askAllen.title') }}</span>
              <span class="allen-panel-sub">{{ t('askAllen.dock.companion') }}</span>
            </span>
          </span>
          <span class="allen-panel-actions">
            <Tooltip :text="t('askAllen.dock.clearChat')" placement="bottom">
              <button type="button" class="allen-panel-btn" :aria-label="t('askAllen.dock.clearChat')" @click="clearAllenThread(mode)">
                <i class="bi bi-eraser"></i>
              </button>
            </Tooltip>
            <button type="button" class="allen-panel-btn" :aria-label="t('askAllen.dock.close')" @click="open = false">
              <i class="bi bi-x-lg"></i>
            </button>
          </span>
        </header>
        <div class="allen-panel-body">
          <AskAllenPanel
            :mode="mode"
            :bare="true"
            :suggested-questions="chips"
            :canned-answers="cannedAnswers"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AskAllenPanel from './AskAllenPanel.vue'
import Tooltip from './ui/Tooltip.vue'
import { allenContext, allenPromptsFor } from '@/composables/allenContext'
import { clearAllenThread } from '@/composables/allenThread'

const props = defineProps({
  mode: { type: String, default: 'live' },
  suggestedQuestions: { type: Array, default: () => [] },
  cannedAnswers: { type: Array, default: () => [] },
})

// Live: context-aware openers per surface (Explore/Zones/Actions/zone/Today).
// Demo: the scripted chips passed in (canned answers are tuned to them).
const chips = computed(() =>
  props.mode === 'demo'
    ? props.suggestedQuestions
    : allenPromptsFor(allenContext.kind, allenContext.zone)
)

const { t } = useI18n()

const open = ref(false)
// Close the panel on navigation so it doesn't linger across surfaces.
const route = useRoute()
watch(() => route.fullPath, () => { open.value = false })
</script>

<style scoped>
.allen-dock { position: fixed; inset: 0; pointer-events: none; z-index: 1200; }
.allen-dock > * { pointer-events: auto; }

/* Launcher */
.allen-launch {
  position: fixed; right: 22px; bottom: 22px;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 11px 16px 11px 13px;
  background: #4F7EB3; color: #fff;
  border: 0; border-radius: 999px;
  box-shadow: 0 6px 18px rgba(29, 43, 58, 0.28);
  font-weight: 700; font-size: 13.5px; cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
}
.allen-launch:hover { background: #43709f; transform: translateY(-1px); box-shadow: 0 8px 22px rgba(29,43,58,.32); }
.allen-launch .bi { font-size: 16px; }

/* Panel — above the backdrop so clicks inside it don't dismiss. */
.allen-panel {
  position: fixed; right: 22px; bottom: 22px; z-index: 2;
  width: 460px; max-width: calc(100vw - 32px);
  height: 82vh; max-height: 820px;
  background: #fff; border: 1px solid #e6edf3; border-radius: 16px;
  box-shadow: 0 18px 48px rgba(13, 38, 76, 0.24);
  display: flex; flex-direction: column; overflow: hidden;
}
.allen-panel-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px; border-bottom: 1px solid #eef2f7; flex: 0 0 auto;
}
.allen-panel-id { display: inline-flex; align-items: center; gap: 10px; }
.allen-panel-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: #eef4fb; color: #4F7EB3;
  display: inline-flex; align-items: center; justify-content: center; font-size: 16px;
}
.allen-panel-title { display: block; font-weight: 800; font-size: 14px; color: #1d2b3a; line-height: 1.1; }
.allen-panel-sub { display: block; font-size: 11.5px; color: #7a8895; }
.allen-panel-actions { display: inline-flex; align-items: center; gap: 2px; }
.allen-panel-btn {
  background: transparent; border: 0; color: #8a99a4; cursor: pointer;
  width: 30px; height: 30px; border-radius: 8px; font-size: 14px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .15s ease, color .15s ease;
}
.allen-panel-btn:hover { background: #f2f5f9; color: #47586a; }
/* The shared Tooltip forces a `help` cursor and pops instantly. For this
   clickable button, keep a normal pointer cursor and delay the bubble a beat. */
.allen-panel-actions :deep(.tt-wrap.tt-has-content),
.allen-panel-actions :deep(.tt-wrap.tt-has-content *) { cursor: pointer; }
.allen-panel-actions :deep(.tt-wrap:hover > .tt-bubble) { transition-delay: 0.4s; }
.allen-panel-body { flex: 1 1 auto; overflow: auto; padding: 12px 14px; }

/* Backdrop — subtle, sits BELOW the panel (z-index 1 < panel's 2). */
.allen-backdrop { position: fixed; inset: 0; z-index: 1; background: rgba(13, 38, 76, 0.06); }

/* Animation */
.allen-slide-enter-active, .allen-slide-leave-active { transition: transform .22s ease, opacity .22s ease; }
.allen-slide-enter-from, .allen-slide-leave-to { transform: translateY(12px) scale(.98); opacity: 0; }

@media (max-width: 560px) {
  .allen-panel { right: 8px; left: 8px; bottom: 8px; width: auto; height: 82vh; }
  /* Phone: a round icon button — the wide pill covered a third of a content line. */
  .allen-launch {
    right: 14px; bottom: 14px;
    width: 52px; height: 52px; padding: 0;
    justify-content: center;
  }
  .allen-launch .bi { font-size: 21px; }
  .allen-launch-txt {
    position: absolute; width: 1px; height: 1px; overflow: hidden;
    clip: rect(0 0 0 0); white-space: nowrap;   /* still read out by screen readers */
  }
}
</style>
